import styled from '@emotion/styled';
import useAlertQueue from 'hooks/useAlertQueue';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import SecondaryButton from '../molecules/Button/SecondaryButton';

interface StyledUploadProps {
  borderRadius?: string;
  hasPreview?: boolean;
}

const StyledUpload = styled.div<StyledUploadProps>`
  background-color: ${({ theme }) => theme.color.inputBackground};
  border-radius: ${({ borderRadius }) => borderRadius}; // Use prop or default to 16px
  border: 1px dashed ${({ theme }) => theme.color.border};
  padding: 16px;
  position: relative;
  min-height: ${({ hasPreview }) => (hasPreview ? '300px' : 'auto')};
  width: 100%;
`;

const StyledLabel = styled.label`
  cursor: pointer;
  display: block;
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 4px;
  padding: 10px;
`;

export interface UploadedFile {
  file: Uint8Array;
  fileName: string;
  fileType: string;
}

interface UploadProps {
  children: React.ReactNode;
  onUpload: (file: UploadedFile) => void;
  borderRadius?: string;
  previewUrl?: string;
  id: string;
  accept?: string;
}

const FileUpload = ({
  children,
  onUpload,
  borderRadius = '16px',
  previewUrl,
  id,
  accept = 'image/*',
}: UploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const alertQueue = useAlertQueue();

  useEffect(() => {
    // Set preview for any valid URL (http, https, or blob)
    if (previewUrl) {
      setPreview(previewUrl);
    } else {
      setPreview(null);
    }
  }, [previewUrl]);

  const onFileChangeOrDrop = useCallback(
    (selectedFile: File | null) => {
      if (selectedFile) {
        // Only create an image preview for image files
        let objectUrl: string | null = null;
        if (selectedFile.type.startsWith('image/')) {
          objectUrl = URL.createObjectURL(selectedFile);
          setPreview(objectUrl);
        }

        const reader = new FileReader();
        reader.onloadend = (e) => {
          if (e.target && e.target.result instanceof ArrayBuffer) {
            const arrayBuffer = e.target.result;
            const fileByteArray = new Uint8Array(arrayBuffer);
            onUpload({
              file: fileByteArray,
              fileName: selectedFile.name,
              fileType: selectedFile.type,
            });
          }
        };

        reader.onerror = () => {
          alertQueue.addErrorAlert('Error', 'Error reading file');
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
          setPreview(null);
        };

        reader.readAsArrayBuffer(selectedFile);
      }
    },
    [onUpload, alertQueue]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      setFile(files[0]);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const { files } = event.dataTransfer;
    if (files.length > 0) {
      setFile(files[0]);
    }
  };

  useEffect(() => {
    if (file) {
      onFileChangeOrDrop(file);
      setFile(null);
    }
  }, [file, onFileChangeOrDrop]);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  // Clean up object URLs when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  return (
    <StyledUpload
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      borderRadius={borderRadius}
      hasPreview={!!preview}
    >
      <StyledLabel htmlFor={id}>
        {!preview && children}
        <input
          id={id}
          type="file"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept={accept}
        />
      </StyledLabel>
      {preview && (
        <PreviewContainer>
          <PreviewImage src={preview} alt="Upload preview" />
          <SecondaryButton
            type="button"
            onClick={() => {
              const input = document.getElementById(id) as HTMLInputElement;
              if (input) {
                input.click();
              }
            }}
          >
            Change Photo
          </SecondaryButton>
        </PreviewContainer>
      )}
    </StyledUpload>
  );
};

export default FileUpload;
